/**
 * Theme Provider Component for Cedar Heights Music Academy Admin System
 * 
 * This component wraps the application with Material UI's ThemeProvider
 * and includes CssBaseline for consistent styling across browsers.
 */

import React, { useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from './index';
import { useTheme } from '../stores/uiStore';

interface ThemeProviderProps {
  children: React.ReactNode;
}

/**
 * Custom ThemeProvider component that provides the Cedar Heights theme
 * to all child components and applies global CSS baseline styles.
 *
 * This component dynamically creates the theme based on the current
 * theme mode (light/dark) from the UI store.
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { theme: themeMode } = useTheme();
  
  // Create theme based on current mode, memoized for performance
  const theme = useMemo(() => {
    return createAppTheme(themeMode);
  }, [themeMode]);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
};

export default ThemeProvider;